/**
 * Company Map Component
 * A React component that displays an interactive map showing company location
 * Uses Leaflet for map rendering with custom marker and popup
 */

import React, { useEffect } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";

/**
 * Default marker icon configuration for the map
 * Configures the marker appearance with proper size and anchor point
 */
const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

/**
 * Set default marker icon for all Leaflet markers
 */
L.Marker.prototype.options.icon = DefaultIcon;

/**
 * Company Map component that displays location on an interactive map
 * @param {Object} props - Component props
 * @param {number} props.latitude - Company location latitude
 * @param {number} props.longitude - Company location longitude
 * @returns {JSX.Element} Rendered map component
 */
const CompanyMap = ({ latitude, longitude }) => {
  /**
   * Effect hook to initialize and manage the Leaflet map
   * Creates map, adds tile layer, sets marker and handles cleanup
   */
  useEffect(() => {
    const mapContainer = document.getElementById("map");
    const map = L.map(mapContainer).setView([latitude, longitude], 13);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap contributors",
    }).addTo(map);

    const marker = L.marker([latitude, longitude]).addTo(map);
    marker.bindPopup("Company Headquarters").openPopup();

    return () => {
      map.remove();
    };
  }, [latitude, longitude]);

  return <div id="map" className="w-full h-screen rounded-lg shadow-lg" />;
};

export default CompanyMap;
